import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

const UGCWithoutFilming = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate("/");
    }, 100);
    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <>
      <Helmet>
        <title>UGC Without Filming | No Camera Needed | AI Video Content | Fuel72</title>
        <meta
          name="description"
          content="Create UGC videos without filming or hiring creators. AI generates professional marketing content with no camera required. 72-hour delivery."
        />
        <meta
          name="keywords"
          content="ugc without filming, no camera ugc, ugc ohne drehen, create ugc no filming, ai generated ugc, no actor videos, ugc without creators"
        />
        <link rel="canonical" href="https://www.fuel72.com/ugc-without-filming" />
        
        {/* Hreflang */}
        <link rel="alternate" hrefLang="en" href="https://www.fuel72.com/ugc-without-filming" />
        <link rel="alternate" hrefLang="de" href="https://www.fuel72.com/ugc-deutschland" />
        <link rel="alternate" hrefLang="x-default" href="https://www.fuel72.com/ugc-without-filming" />
        
        {/* Open Graph */}
        <meta property="og:title" content="UGC Without Filming | AI Video Content | Fuel72" />
        <meta property="og:description" content="Create professional UGC videos without a camera. AI does the work for you." />
        <meta property="og:url" content="https://www.fuel72.com/ugc-without-filming" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.fuel72.com/og-image.jpg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="UGC Without Filming | Fuel72" />
        <meta name="twitter:description" content="No camera? No problem. Create UGC with AI in 72 hours." />

        {/* Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "AI UGC Video Creation",
            "name": "UGC Without Filming - AI Generated Content",
            "description": "Create professional UGC videos without any filming. AI generates authentic-looking marketing content.",
            "provider": { "@type": "Organization", "name": "Fuel72" },
            "areaServed": ["US", "CA", "AU", "GB", "DE", "AT", "CH", "Worldwide"]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Can I really create UGC without filming?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes! Fuel72 uses AI to create authentic UGC-style videos. No camera, no actors, no studio needed. Just fill out a brief and receive professional videos in 72 hours."
                }
              },
              {
                "@type": "Question",
                "name": "Does AI UGC look real?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Modern AI creates incredibly realistic content that feels authentic. Our videos are designed to match the UGC style that performs best on social media."
                }
              },
              {
                "@type": "Question",
                "name": "What do I need to create UGC without filming?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Just your logo, brand colors, and a brief description of your business. Fuel72's AI handles everything else - no equipment, no experience needed."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <main className="min-h-screen gradient-mesh flex items-center justify-center">
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6">
            UGC{" "}
            <span className="bg-gradient-to-r from-neon-purple via-neon-pink to-orange-500 bg-clip-text text-transparent">
              Without Filming
            </span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Create professional UGC videos with AI. 
            No camera, no actors, no filming required.
          </p>
          
          <div className="max-w-3xl mx-auto text-left space-y-6">
            <h2 className="text-2xl font-bold">Why Choose UGC Without Filming?</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li>📵 <strong>No Equipment Needed</strong> - Skip cameras, lights, and microphones</li>
              <li>👤 <strong>No Actors Required</strong> - AI creates realistic presenters</li>
              <li>🏠 <strong>Work From Anywhere</strong> - No studio or location needed</li>
              <li>⏱️ <strong>Save Time</strong> - No scripting, shooting, or editing</li>
              <li>💰 <strong>Save Money</strong> - No production costs or creator fees</li>
            </ul>
            
            <h2 className="text-2xl font-bold mt-8">How It Works</h2>
            <div className="grid md:grid-cols-3 gap-6 mt-4">
              <div className="p-4 rounded-lg bg-card/50 text-center">
                <div className="text-3xl mb-2">1️⃣</div>
                <h3 className="font-semibold">Fill Brief</h3>
                <p className="text-sm text-muted-foreground">Share your brand details and goals</p>
              </div>
              <div className="p-4 rounded-lg bg-card/50 text-center">
                <div className="text-3xl mb-2">2️⃣</div>
                <h3 className="font-semibold">AI Creates</h3>
                <p className="text-sm text-muted-foreground">Our AI generates your videos</p>
              </div>
              <div className="p-4 rounded-lg bg-card/50 text-center">
                <div className="text-3xl mb-2">3️⃣</div>
                <h3 className="font-semibold">Download</h3>
                <p className="text-sm text-muted-foreground">Get videos in 72 hours</p>
              </div>
            </div>
            
            <h2 className="text-2xl font-bold mt-8">Perfect For</h2>
            <p className="text-muted-foreground">
              Business owners who want professional video content but don't have time to film. 
              Marketers who need fast UGC at scale. Anyone camera-shy who still wants to grow their brand.
            </p>
          </div>
          
          <p className="text-sm text-muted-foreground mt-12">Redirecting to homepage...</p>
        </div>
      </main>
    </>
  );
};

export default UGCWithoutFilming;
